<template>
  <div
    v-if="isIosBrowser || skipAppDownload"
    class="flex min-h-screen flex-col items-center justify-center gap-6 px-6"
  >
    <p>
      {{
        t(
          isIosBrowser
            ? "onboarding.download_app.browser_ready"
            : "onboarding.download_app.skipping_app_download",
        )
      }}
    </p>
    <PvButton
      :loading="completingRegistrationProcess"
      @click="handleClickedDownloadLater"
    >
      {{ t("wizard_layout.continue") }}
    </PvButton>
  </div>
  <RegistrationLayout v-else :current-step="currentStep">
    <template #step-content>
      <div class="flex flex-col items-center gap-6 px-4">
        <h1>{{ t("onboarding.download_app.title") }}</h1>
        <div class="flex w-full max-w-md flex-col gap-2 text-xl">
          <p>{{ t("onboarding.download_app.registration_motivator") }}</p>
          <p>
            {{ t("onboarding.download_app.registration_download_later_hint") }}
          </p>
        </div>

        <img
          :src="macbookWithControlaApp"
          class="w-full max-w-md rounded-2xl object-fill"
          alt=""
          height="294"
          width="392"
        />

        <div class="flex w-full max-w-md flex-col items-center">
          <AppDownloadOptions
            :loading="completingRegistrationProcess"
            @clicked-download="handleClickedDownload"
          />

          <button class="mt-14" @click="confirmDownloadLater = true">
            {{ t("onboarding.download_app.download_later.button") }}
          </button>
        </div>
      </div>
    </template>
  </RegistrationLayout>

  <Dialog
    v-model:visible="confirmDownloadLater"
    class="w-full max-w-lg"
    :closable="false"
  >
    <template #header>
      <h2 class="text-3xl! font-medium!">
        {{ t("onboarding.download_app.download_later.are_you_sure") }}
      </h2>
    </template>
    <template #default>
      <div class="flex flex-col gap-9">
        <img
          :src="macbookWithControlaApp"
          class="w-full max-w-md rounded-2xl object-fill"
          alt=""
          height="261"
          width="392"
        />
        <p>
          {{ t("onboarding.download_app.download_later.limited_web_service") }}
        </p>
        <div>
          <p>{{ t("onboarding.download_app.download_later.reason_header") }}</p>
          <ul>
            <li>{{ t("onboarding.download_app.download_later.reason_1") }}</li>
            <li>{{ t("onboarding.download_app.download_later.reason_2") }}</li>
            <li>{{ t("onboarding.download_app.download_later.reason_3") }}</li>
          </ul>
        </div>
        <div class="flex flex-row gap-3">
          <PvButton
            :label="
              t(
                'onboarding.download_app.download_later.confirm_not_downloading',
              )
            "
            class="bg-brand-beige! text-primary-black!"
            :loading="completingRegistrationProcess"
            @click="handleClickedDownloadLater"
          />
          <PvButton
            :label="t('onboarding.download_app.download_later.download_app')"
            class="bg-brand-green!"
            @click="confirmDownloadLater = false"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useSeoMeta } from "@unhead/vue";
import PvButton from "primevue/button";
import { useToast } from "primevue/usetoast";
import macbookWithControlaApp from "shared-ui/assets/macbook-pro-mockup-of-a-man-working-with-controla.avif";
import type { DownloadAssetsSingleAsset } from "shared-ui/types/download-app.ts";
import { useApi } from "shared-ui/utils/api";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import AppDownloadOptions from "@/components/app-setup/AppDownloadOptions.vue";
import { useActivelyUsingAgentPoll } from "@/composables/use-actively-using-agent-poll.ts";
import RegistrationLayout from "@/layouts/RegistrationLayout.vue";
import { startAppDownload } from "@/utils/app-download.ts";
import { detectIOSBrowser } from "@/utils/mobile-browser.ts";
import { completeRegistrationProcess } from "@/utils/registration.ts";
import { shouldSkipAppDownload } from "@/utils/skip-app-download.ts";

const { t } = useI18n();

definePage({
  meta: {
    requiresAuth: true,
  },
});

useSeoMeta({
  title: () => t("onboarding.download_app.title"),
});

const confirmDownloadLater = ref<boolean>(false);

const isIosBrowser = detectIOSBrowser();
const skipAppDownload = shouldSkipAppDownload();
const currentStep: number = 3;
const completingRegistrationProcess = ref<boolean>(false);

const api = useApi();
const route = useRoute();
const router = useRouter();
const toast = useToast();

// The agent can already be installed and logged in when the user reaches this
// step (e.g. agent login started the signup, and the post-login OAuth redirect
// completed the agent's login mid-registration). Detect that and skip the
// download step instead of leaving the wizard stuck here.
const retryTimeout = Math.max(
  250,
  Number(import.meta.env.VITE_RETRY_TIMEOUT_MULTIPLIER ?? 1) * 1000,
);
useActivelyUsingAgentPoll(api, retryTimeout, async () => {
  if (isIosBrowser || skipAppDownload || completingRegistrationProcess.value) {
    return false;
  }

  completingRegistrationProcess.value = true;
  try {
    await completeRegistrationProcess(api);
  } finally {
    completingRegistrationProcess.value = false;
  }

  await router.push({ name: "/registration/app-installed" });
  return true;
});

async function completeRegistrationFromUserAction(): Promise<boolean> {
  completingRegistrationProcess.value = true;
  try {
    await completeRegistrationProcess(api);
    return true;
  } catch (error) {
    console.error("Failed to complete registration", error);
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: t("onboarding.download_app.completion_error"),
      life: 10_000,
    });
    return false;
  } finally {
    completingRegistrationProcess.value = false;
  }
}

async function handleClickedDownload(
  asset: DownloadAssetsSingleAsset,
): Promise<void> {
  if (!(await completeRegistrationFromUserAction())) return;

  // Navigate
  await router.push({
    name: "/registration/install-app-instructions/[os]",
    params: { os: asset.os },
  });

  startAppDownload(asset);
}

async function handleClickedDownloadLater(): Promise<void> {
  if (!(await completeRegistrationFromUserAction())) return;
  await router.push({ name: "/welcome" });
}

onMounted(async () => {
  if (isIosBrowser || skipAppDownload) {
    await handleClickedDownloadLater();
    return;
  }
  if (route.query.msg) {
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: route.query.msg.toString(),
      life: 10_000,
    });
  }
});
</script>
